import { DutyRequirement } from '../types';
import { normaliseTime } from './timeUtils';

/**
 * Validates duty requirements form data
 * @param timePeriods - Array of duty requirements to validate
 * @returns Object containing validation status and error messages
 */
export const validateDutyRequirements = (
  timePeriods: DutyRequirement[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Allow empty array - this represents removing all duty requirements
  if (timePeriods.length === 0) {
    return { isValid: true, errors: [] };
  }

  timePeriods.forEach((period, index) => {
    // Check for missing time values
    if (!period.startTime || period.startTime.trim() === '') {
      errors.push(`Time period ${index + 1}: Start time is required`);
    }
    if (!period.endTime || period.endTime.trim() === '') {
      errors.push(`Time period ${index + 1}: End time is required`);
    }

    // Only compare times if both are present
    if (period.startTime && period.endTime && period.startTime >= period.endTime) {
      errors.push(`Time period ${index + 1}: Start time must be before end time`);
    }
    if (period.minPersonnel < 1) {
      errors.push(`Time period ${index + 1}: Required people must be at least 1`);
    }
  });

  // Check for overlapping time periods
  // Only check if we have valid times
  for (let i = 0; i < timePeriods.length; i++) {
    for (let j = i + 1; j < timePeriods.length; j++) {
      const period1 = timePeriods[i];
      const period2 = timePeriods[j];

      // Skip validation if any time values are missing
      if (!period1.startTime || !period1.endTime || !period2.startTime || !period2.endTime) {
        continue;
      }

      // Normalise times to HH:mm format for comparison
      const start1 = normaliseTime(period1.startTime);
      const end1 = normaliseTime(period1.endTime);
      const start2 = normaliseTime(period2.startTime);
      const end2 = normaliseTime(period2.endTime);

      // Periods overlap if: start1 < end2 AND start2 < end1
      // Using strict inequality (<) allows adjacent periods where end1 === start2
      if (start1 < end2 && start2 < end1) {
        errors.push(
          `Time periods ${i + 1} (${start1}-${end1}) and ${j + 1} (${start2}-${end2}) overlap`
        );
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};
